import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { CAPS } from '@/lib/kbCaps';
import type {
  CatalogItem,
  KbProfile,
  KbProfileSubmitRequest,
  Modality,
  Objective,
  Offer,
  OperationFields,
  SessionSchedule,
} from '@/services/kbProfileService';

/**
 * F035 — the reflective KB form (minimum-viable flow).
 *
 * The conditional schema is keyed off the **multi-select `modalities`**: which
 * operation fields exist (and are validated) is *derived* from the selected
 * modalities, never hidden with CSS (frontend.md responsive rule). Picking
 * `digital_on_demand` alone surfaces only `online_platform` + `access_method`
 * — no address / hours / shipping. Changing modalities re-derives the set and
 * `reconcileOperation` drops orphan values so the conditional schema can never
 * strand a field the new modality doesn't own.
 *
 * --- Input caps = the KB size guarantee (by construction) -------------------
 * These caps MIRROR the backend `kb_profile_assembler.CAPS` exactly (same
 * numbers both sides — single source: @/lib/kbCaps). Worst case = every field
 * at its cap + every list at its max count; that sum (+ markdown scaffolding)
 * is what keeps the assembled `knowledge_md` ≤ KB_SIZE_CEILING. The backend
 * Marshmallow schema re-enforces the identical caps; this is the first line.
 */
export const KB_SIZE_CEILING = 17000;

// F071: caps live in the shared @/lib/kbCaps module (single source mirroring
// the backend); re-exported here so existing imports keep working.
export { CAPS };

export const OFFERS = ['services', 'products_physical', 'products_digital'] as const;

export const MODALITIES = [
  'presencial_fixo',
  'domicilio',
  'online_live',
  'digital_on_demand',
  'physical_shipping',
  'made_to_order',
  'event_dated',
  'rental',
] as const;

/** F076/F077 — the 4 behavioral objective families (mirrors the backend
 * `OBJECTIVES` tuple). Default selection is `capture_contacts`; the modality
 * pre-selection heuristic was retired with the 8-way taxonomy. */
export const OBJECTIVES = [
  'capture_contacts',
  'send_to_link',
  'urgent_dispatch',
  'answer_only',
] as const;

export const PRICE_TYPES = ['fixed', 'from', 'quote', 'subscription', 'package'] as const;

/** Known payment toggles. Keys match the assembler's label map so the derived
 * KB renders them with friendly names; anything else goes via free-add. */
export const PAYMENT_METHODS = [
  'pix',
  'mbway',
  'credit_card',
  'debit_card',
  'cash',
  'bank_transfer',
  'multibanco',
] as const;

/** Return only the payment methods relevant to the tenant's locale.
 *
 * - `pix`       — Brazil only (`pt-BR`)
 * - `mbway`     — Portugal only (`pt-PT`)
 * - `multibanco`— Portugal only (`pt-PT`)
 * - All others  — both locales
 */
export function paymentMethodsForLocale(language: string): readonly string[] {
  return PAYMENT_METHODS.filter((m) => {
    if (m === 'pix') return language.startsWith('pt-BR');
    if (m === 'mbway' || m === 'multibanco') return language.startsWith('pt-PT');
    return true;
  });
}

/** All operation free-text field keys (ordered as the assembler renders them). */
export const OPERATION_TEXT_FIELDS = [
  'address',
  'coverage_area',
  'travel_fee',
  'online_platform',
  'access_method',
  'shipping',
  'dispatch_time',
  'returns',
  'order_lead_time',
  'event_advance',
  'headcount_pricing',
  'rental_period',
  'rental_deposit',
] as const;

export type OperationTextField = (typeof OPERATION_TEXT_FIELDS)[number];

/** Which operation text fields each modality surfaces (the derived field set).
 * Digital is deliberately empty of physical fields — its only operation
 * fields are `online_platform` + `access_method`. */
const MODALITY_OPERATION_FIELDS: Record<Modality, OperationTextField[]> = {
  presencial_fixo: ['address'],
  domicilio: ['coverage_area', 'travel_fee'],
  online_live: ['online_platform'],
  digital_on_demand: ['online_platform', 'access_method'],
  physical_shipping: ['shipping', 'dispatch_time', 'returns'],
  made_to_order: ['order_lead_time'],
  event_dated: ['event_advance', 'headcount_pricing'],
  rental: ['rental_period', 'rental_deposit'],
};

/** Modalities for which a working-hours grid is meaningful (live attendance). */
const WORKING_HOURS_MODALITIES: Modality[] = ['presencial_fixo', 'domicilio', 'online_live'];

// ---------------------------------------------------------------------------
// Derivations (pure — shared by the component and its tests)
// ---------------------------------------------------------------------------

/** The active operation text fields for the current modality selection, in
 * assembler order, de-duplicated across combined modalities. */
export function operationFieldsForModalities(modalities: Modality[]): OperationTextField[] {
  const active = new Set<OperationTextField>();
  for (const m of modalities) {
    for (const f of MODALITY_OPERATION_FIELDS[m] ?? []) active.add(f);
  }
  return OPERATION_TEXT_FIELDS.filter((f) => active.has(f));
}

export function showWorkingHours(modalities: Modality[]): boolean {
  return modalities.some((m) => WORKING_HOURS_MODALITIES.includes(m));
}

/** F069 #6 — recurring session schedules (classes, group slots, appointments
 * with fixed starts) only make sense when the tenant sells services AND has a
 * live-attendance modality. Product-only tenants never see the editor. */
export function showSessionSchedules(offers: Offer[], modalities: Modality[]): boolean {
  return offers.includes('services') && showWorkingHours(modalities);
}

/** Drop operation values no longer owned by the active modality set (orphan
 * prevention). Payment fields are modality-independent and always kept.
 * F069 #6: when `offers` is provided, `sessions` additionally requires the
 * services offer (omitting `offers` keeps the legacy modality-only rule). */
export function reconcileOperation(
  operation: OperationFields,
  modalities: Modality[],
  offers?: Offer[]
): OperationFields {
  const activeText = new Set<OperationTextField>(operationFieldsForModalities(modalities));
  const next: OperationFields = {};
  for (const key of OPERATION_TEXT_FIELDS) {
    if (activeText.has(key) && operation[key]) next[key] = operation[key];
  }
  if (showWorkingHours(modalities)) {
    if (operation.working_hours) next.working_hours = operation.working_hours;
    const sessionsAllowed = offers === undefined || showSessionSchedules(offers, modalities);
    if (sessionsAllowed && operation.sessions?.length) next.sessions = operation.sessions;
  }
  if (operation.payment_methods?.length) next.payment_methods = operation.payment_methods;
  if (operation.payment_methods_other?.length) {
    next.payment_methods_other = operation.payment_methods_other;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Form values + schema
// ---------------------------------------------------------------------------

export interface KbProfileFormValues {
  offers: Offer[];
  modalities: Modality[];
  one_liner: string;
  ideal_customer: string;
  catalog: CatalogItem[];
  operation: OperationFields;
  bot_goal: Objective | '';
  objective_freetext: string;
  handoff_contact: string;
}

const TOO_LONG = { message: 'onboarding.errors.field_too_long' };
const REQUIRED = { message: 'onboarding.errors.field_required' };

const catalogItemSchema = z.object({
  name: z.string().min(1, REQUIRED).max(CAPS.catalog_name, TOO_LONG),
  price: z.string().max(CAPS.catalog_price, TOO_LONG).optional().default(''),
  price_type: z.enum(PRICE_TYPES).optional().default('quote'),
  duration: z.string().max(CAPS.catalog_duration, TOO_LONG).optional().default(''),
  lead_time: z.string().max(CAPS.catalog_lead_time, TOO_LONG).optional().default(''),
  description: z.string().max(CAPS.catalog_description, TOO_LONG).optional().default(''),
});

const HANDOFF_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const HANDOFF_PHONE = /^\+?[\d\s().-]{6,}$/;

const operationTextCap = z.string().max(CAPS.operation_field, TOO_LONG).optional();

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

/** Single session entry schema — mirrors backend SessionSchema. */
const sessionSchema = z.object({
  days: z
    .array(z.enum(WEEKDAY_KEYS))
    .min(1, { message: 'onboarding.errors.kb_session_days_required' }),
  start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
    message: 'onboarding.errors.kb_session_start_invalid',
  }),
  duration_min: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().int().min(1).max(1440).optional()
  ),
  // `.nullish()` (NOT `.optional()`): the backend `SessionSchema.note` is
  // `allow_none=True` and its `@pre_load` maps empty "" → None, so a saved
  // session round-trips as `note: null`. `.optional()` accepts only
  // `undefined` and would reject that null — making every re-edited session
  // without a note fail validation invisibly (the field shows only its
  // placeholder), turning the "Sessões" label red and hard-blocking "Próximo".
  // Coerce empty string → undefined first so the max-length check ignores it.
  note: z.preprocess(
    (v) => (v == null || v === '' ? undefined : v),
    z.string().max(CAPS.session_note, TOO_LONG).optional()
  ),
});

/** The conditional operation sub-schema — built from the active modality set
 * so only the surfaced fields are validated (the rest are pruned on submit). */
function buildOperationSchema(modalities: Modality[]) {
  const active = operationFieldsForModalities(modalities);
  const shape: Record<string, z.ZodTypeAny> = {
    payment_methods: z.array(z.string().max(CAPS.payment_other, TOO_LONG)).optional(),
    payment_methods_other: z
      .array(z.string().min(1).max(CAPS.payment_other, TOO_LONG))
      .max(CAPS.payment_other_items, { message: 'onboarding.errors.kb_items_too_many' })
      .optional(),
  };
  for (const key of active) shape[key] = operationTextCap;
  if (showWorkingHours(modalities)) {
    shape.working_hours = z.record(z.unknown()).nullish();
    // Two distinct hazards the raw `z.array().optional()` mishandled:
    //   1. A non-array value — a hydrated profile whose `operation.sessions` is
    //      `null` (absent/empty JSONB round-trips as null). `optional()` only
    //      allows `undefined`, so `null` errored "expected array" and the
    //      sessions FormItem went red with NO editable row to fix → hard-blocked
    //      "Próximo" from the *hours* step. Coerce any non-array → `[]`.
    //   2. A FULLY-blank row (no days AND no start) — an accidental "Adicionar"
    //      click. `cleanSessions` drops it at submit, so prune it here too.
    // A *partially*-filled row (days set OR start set, but not complete) is the
    // owner's real intent left unfinished — keep it so `sessionSchema` errors
    // and the editor surfaces a per-row hint; silently pruning it would lose a
    // session the owner meant to keep.
    shape.sessions = z.preprocess(
      (val) =>
        Array.isArray(val)
          ? val.filter((s) => {
              const row = (s ?? {}) as { days?: unknown; start?: unknown };
              const hasDays = Array.isArray(row.days) && row.days.length > 0;
              const hasStart = typeof row.start === 'string' && row.start.trim() !== '';
              return hasDays || hasStart;
            })
          : [],
      z
        .array(sessionSchema)
        .max(CAPS.session_items, { message: 'onboarding.errors.kb_items_too_many' })
        .optional()
    );
  }
  // passthrough: RHF carries every operation key in state; submit prunes the
  // inactive ones via reconcileOperation, so non-active keys don't fail here.
  return z.object(shape).passthrough();
}

// Re-export WeekdayKey so SessionScheduleEditor can use it without re-declaring.
export type { WeekdayKey };

export function buildKbProfileSchema(modalities: Modality[]) {
  return z.object({
    offers: z.array(z.enum(OFFERS)).min(1, { message: 'onboarding.errors.kb_offers_required' }),
    modalities: z
      .array(z.enum(MODALITIES))
      .min(1, { message: 'onboarding.errors.kb_modalities_required' }),
    one_liner: z.string().max(CAPS.one_liner, TOO_LONG).optional().default(''),
    ideal_customer: z.string().max(CAPS.ideal_customer, TOO_LONG).optional().default(''),
    catalog: z
      .array(catalogItemSchema)
      .max(CAPS.catalog_items, { message: 'onboarding.errors.kb_items_too_many' })
      .optional()
      .default([]),
    operation: buildOperationSchema(modalities),
    bot_goal: z.enum(OBJECTIVES, { message: 'onboarding.errors.kb_objective_required' }),
    objective_freetext: z.string().max(CAPS.objective_freetext, TOO_LONG).optional().default(''),
    handoff_contact: z
      .string()
      .min(1, { message: 'onboarding.errors.kb_handoff_required' })
      .max(CAPS.handoff_contact, TOO_LONG)
      .refine((v) => HANDOFF_EMAIL.test(v) || HANDOFF_PHONE.test(v), {
        message: 'onboarding.errors.kb_handoff_invalid',
      }),
  });
}

export const DEFAULT_KB_PROFILE_VALUES: KbProfileFormValues = {
  offers: [],
  modalities: [],
  one_liner: '',
  ideal_customer: '',
  catalog: [],
  operation: { payment_methods: [], payment_methods_other: [] },
  bot_goal: '',
  objective_freetext: '',
  handoff_contact: '',
};

/** Hydrate form values from a committed profile (re-edit) — only the
 * minimum-viable subset; enrichment fields are ignored. */
export function fromKbProfile(profile: KbProfile | null): Partial<KbProfileFormValues> {
  if (!profile) return {};
  return {
    offers: profile.offers ?? [],
    modalities: profile.modalities ?? [],
    one_liner: profile.one_liner ?? '',
    ideal_customer: profile.ideal_customer ?? '',
    catalog: profile.catalog ?? [],
    operation: {
      payment_methods: [],
      payment_methods_other: [],
      ...(profile.operation ?? {}),
    },
    bot_goal: profile.bot_goal ?? '',
    objective_freetext: profile.objective_freetext ?? '',
    handoff_contact: profile.handoff_contact ?? '',
  };
}

/** Clean session entries before submission:
 * - drop rows with empty days or empty start
 * - coerce duration_min to a number (omit when blank/undefined)
 * - trim and omit empty note
 */
function cleanSessions(sessions: SessionSchedule[] | undefined): SessionSchedule[] | undefined {
  if (!sessions?.length) return undefined;
  const cleaned = sessions
    .filter((s) => s.days?.length && s.start?.trim())
    .map((s) => {
      const entry: SessionSchedule = { days: s.days, start: s.start.trim() };
      const dm = s.duration_min;
      if (dm !== undefined && dm !== null && String(dm) !== '') {
        entry.duration_min = Number(dm);
      }
      const note = s.note?.trim();
      if (note) entry.note = note;
      return entry;
    });
  return cleaned.length ? cleaned : undefined;
}

/** Build the activation payload: prune operation to the active modality set,
 * drop empty optionals, trim the catalog to non-empty rows. */
export function toSubmitPayload(values: KbProfileFormValues): KbProfileSubmitRequest {
  const rawOperation = reconcileOperation(values.operation ?? {}, values.modalities, values.offers);
  const sessions = cleanSessions(rawOperation.sessions);
  const operation = sessions ? { ...rawOperation, sessions } : rawOperation;
  // Remove sessions key entirely when no valid sessions remain
  if (!sessions && 'sessions' in operation) delete (operation as OperationFields).sessions;
  const catalog = (values.catalog ?? [])
    .filter((item) => item.name?.trim())
    .map((item) => {
      const out: CatalogItem = { name: item.name.trim(), price_type: item.price_type ?? 'quote' };
      // F069 #8: "sob consulta" carries no number — a stale typed price must
      // not leak into the KB when the owner switches the type to quote.
      if (out.price_type !== 'quote' && item.price?.trim()) out.price = item.price.trim();
      if (item.duration?.trim()) out.duration = item.duration.trim();
      if (item.lead_time?.trim()) out.lead_time = item.lead_time.trim();
      if (item.description?.trim()) out.description = item.description.trim();
      return out;
    });

  const payload: KbProfileSubmitRequest = {
    offers: values.offers,
    modalities: values.modalities,
    catalog,
    operation,
    bot_goal: values.bot_goal as Objective,
    handoff_contact: values.handoff_contact.trim(),
  };
  if (values.one_liner?.trim()) payload.one_liner = values.one_liner.trim();
  if (values.ideal_customer?.trim()) payload.ideal_customer = values.ideal_customer.trim();
  if (values.objective_freetext?.trim()) {
    payload.objective_freetext = values.objective_freetext.trim();
  }
  return payload;
}

/** Rough estimate of the assembled KB char count for the live size nudge
 * ("KB enxuto responde melhor"). Not exact — a soft UX indicator only. */
export function estimateKbChars(values: KbProfileFormValues): number {
  const SCAFFOLD_PER_FIELD = 14; // label + markdown punctuation headroom
  let total = 0;
  const add = (s?: string) => {
    if (s?.trim()) total += s.trim().length + SCAFFOLD_PER_FIELD;
  };
  add(values.one_liner);
  add(values.ideal_customer);
  add(values.objective_freetext);
  add(values.handoff_contact);
  for (const item of values.catalog ?? []) {
    add(item.name);
    add(item.price);
    add(item.duration);
    add(item.lead_time);
    add(item.description);
  }
  const op = values.operation ?? {};
  for (const key of OPERATION_TEXT_FIELDS) add(op[key]);
  for (const p of op.payment_methods ?? []) add(p);
  for (const p of op.payment_methods_other ?? []) add(p);
  return total;
}

export interface UseKbProfileFormOptions {
  modalities: Modality[];
  initial?: Partial<KbProfileFormValues>;
}

/** The RHF instance. The resolver is rebuilt by the caller when `modalities`
 * change (conditional schema) — see `KbSection`. */
export function useKbProfileForm({ modalities, initial }: UseKbProfileFormOptions) {
  return useForm<KbProfileFormValues>({
    resolver: zodResolver(buildKbProfileSchema(modalities) as never),
    defaultValues: { ...DEFAULT_KB_PROFILE_VALUES, ...(initial ?? {}) },
  });
}
